import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import Chip from "@mui/material/Chip";
import { ip_back } from "../../config";
import { CLASSEMENTS, classementLabel } from "../../utils/levelBands";
import { normalizeAppLang } from "../../utils/appLanguages";

export function emptyGroupLevel() {
  return { classement: "", objectiveCbIds: [] };
}

/** Map groupDetails payload → form state. */
export function groupLevelFromDetails(details) {
  return {
    classement: details?.gr_level_classement || "",
    objectiveCbIds: Array.isArray(details?.gr_objective_cb_ids)
      ? details.gr_objective_cb_ids.map(String)
      : [],
  };
}

function blockName(block, lng) {
  if (!block) return "";
  return block[`cb_name_${lng}`] || block.cb_name_en || block.cb_name_fr || block.cb_id;
}

/**
 * Coach-declared group level (overrides the players' median classement) and
 * the competency blocks the group is working toward.
 */
export function GroupLevelFields({ value, onChange, clubId }) {
  const { t, i18n } = useTranslation();
  const lng = normalizeAppLang(i18n.language);
  const [matrix, setMatrix] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const params = clubId ? `?club_id=${encodeURIComponent(clubId)}` : "";
    axios
      .get(`${ip_back}api/level-bands/matrix${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => {
        if (!cancelled) setMatrix(res.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setMatrix(null);
      });
    return () => {
      cancelled = true;
    };
  }, [clubId]);

  const form = value || emptyGroupLevel();
  const band = useMemo(() => {
    if (!form.classement || !matrix) return null;
    return matrix.classements.find((c) => c.classement === form.classement)?.band_key || null;
  }, [form.classement, matrix]);

  const statusById = useMemo(() => {
    const map = new Map();
    if (!band || !matrix) return map;
    for (const row of matrix.band_cbs) {
      if (row.band_key === band) map.set(String(row.cb_id), row.status);
    }
    return map;
  }, [band, matrix]);

  const toggleObjective = (cbId) => {
    const id = String(cbId);
    const next = form.objectiveCbIds.includes(id)
      ? form.objectiveCbIds.filter((x) => x !== id)
      : [...form.objectiveCbIds, id];
    onChange?.({ ...form, objectiveCbIds: next });
  };

  return (
    <div className="eg-level">
      <div className="eg-field">
        <label htmlFor="group-level-classement">{t("groupLevelClassement")}</label>
        <Select
          id="group-level-classement"
          fullWidth
          displayEmpty
          value={form.classement}
          onChange={(e) => onChange?.({ ...form, classement: e.target.value })}
          input={<OutlinedInput notched={false} />}
          renderValue={(v) =>
            v ? (
              `${classementLabel(v, t)}${band ? ` · ${t(`levelBand_${band}`)}` : ""}`
            ) : (
              <span className="eg-placeholder">{t("groupLevelAuto")}</span>
            )
          }
          MenuProps={{ PaperProps: { className: "eg-menu" } }}
        >
          <MenuItem value="" className="eg-menu-item">
            <em>{t("groupLevelAuto")}</em>
          </MenuItem>
          {CLASSEMENTS.map((c) => (
            <MenuItem key={c} value={c} className="eg-menu-item">
              {classementLabel(c, t)}
            </MenuItem>
          ))}
        </Select>
        <p className="eg-hint">{t("groupLevelHint")}</p>
      </div>

      <div className="eg-field">
        <label>{t("groupObjectives")}</label>
        <div className="eg-level__chips">
          {(matrix?.blocks || []).map((block) => {
            const id = String(block.cb_id);
            const selected = form.objectiveCbIds.includes(id);
            const status = statusById.get(id);
            return (
              <Chip
                key={id}
                label={blockName(block, lng)}
                color={selected ? "primary" : "default"}
                variant={selected ? "filled" : "outlined"}
                onClick={() => toggleObjective(id)}
                className={status === "locked" ? "eg-level__chip--locked" : undefined}
                title={status ? t(`levelStatus_${status}`) : undefined}
              />
            );
          })}
        </div>
        <p className="eg-hint">{t("groupObjectivesHint")}</p>
      </div>
    </div>
  );
}

export default GroupLevelFields;
